import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Car,
  Check,
  Crown,
  Shield,
  ArrowLeft,
  Loader2,
  Sparkles,
  BookOpen,
  CreditCard,
  X,
  CheckCircle,
  XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const planIcons = {
  start: Sparkles,
  standard: BookOpen,
  premium: Crown
};

const planColors = {
  start: {
    gradient: "from-slate-700/50 to-slate-600/30",
    border: "border-slate-500/30",
    icon: "bg-slate-500/20 text-slate-300",
    button: "bg-slate-600 hover:bg-slate-500"
  },
  standard: {
    gradient: "from-blue-900/50 to-blue-800/30",
    border: "border-blue-500/30",
    icon: "bg-blue-500/20 text-blue-400",
    button: "bg-blue-600 hover:bg-blue-700"
  },
  premium: {
    gradient: "from-yellow-900/40 to-amber-800/30",
    border: "border-yellow-500/40",
    icon: "bg-yellow-500/20 text-yellow-400",
    button: "bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700"
  }
};

export default function SubscriptionsPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'checking', 'success', 'failed'
  const [paymentMessage, setPaymentMessage] = useState("");

  // Verifică dacă revenim de la Stripe
  const checkPaymentReturn = useCallback(async () => {
    const payment = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");

    if (payment === "success" && sessionId) {
      setPaymentStatus("checking");
      setPaymentMessage("Verificăm plata...");

      // Polling pentru status
      let attempts = 0;
      const maxAttempts = 10;
      const pollInterval = 2000;

      const pollStatus = async () => {
        try {
          const token = localStorage.getItem("token");

          const response = await axios.get(
            `${API}/payments/status/${sessionId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          if (response.data.payment_status === "paid") {
            setPaymentStatus("success");
            setPaymentMessage(response.data.message || "Plata a fost procesată cu succes!");
            toast.success("Felicitări! Abonamentul tău a fost activat!");
            await refreshUser();
            // Curățăm URL-ul
            setSearchParams({});
            return;
          }

          if (response.data.status === "expired" || response.data.status === "failed") {
            setPaymentStatus("failed");
            setPaymentMessage("Plata nu a fost finalizată. Te rugăm să încerci din nou.");
            setSearchParams({});
            return;
          }

          // Continuă polling
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, pollInterval);
          } else {
            setPaymentStatus("failed");
            setPaymentMessage("Verificarea plății a expirat. Contactează suportul dacă ai fost debitat.");
          }
        } catch (error) {
          console.error("Error checking payment:", error);
          setPaymentStatus("failed");
          setPaymentMessage("Eroare la verificarea plății.");
        }
      };

      pollStatus();
    } else if (payment === "cancelled") {
      setPaymentStatus("failed");
      setPaymentMessage("Plata a fost anulată.");
      toast.info("Plata a fost anulată");
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, refreshUser]);

  useEffect(() => {
    fetchPlans();
    checkPaymentReturn();
  }, [checkPaymentReturn]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error("Eroare la încărcarea planurilor");
    } finally {
      setLoading(false);
    }
  };

  const handleActivatePlan = async (planCode) => {
    setProcessingPlan(planCode);

    try {
      if (planCode === "start") {
        // Activare plan gratuit
        await axios.post(`${API}/subscriptions/activate-start`);
        toast.success("Pachetul Start a fost activat!");
        await refreshUser();
      } else {
        // Pentru planuri plătite - Stripe Checkout
        const response = await axios.post(`${API}/payments/create-checkout`, {
          package_id: planCode,
          origin_url: window.location.origin
        });

        if (response.data.checkout_url) {
          // Redirecționăm către Stripe
          window.location.href = response.data.checkout_url;
          return; // Nu resetăm processingPlan - userul pleacă de pe pagină
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Eroare la procesare";
      toast.error(errorMessage);
    } finally {
      setProcessingPlan(null);
    }
  };

  const isCurrentPlan = (planCode) => {
    return user?.subscription_status === "active" && user?.subscription_plan === planCode;
  };

  // Status de plată - modal/banner
  const renderPaymentStatus = () => {
    if (!paymentStatus) return null;

    const statusConfig = {
      checking: {
        icon: <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />,
        bgColor: "bg-blue-500/10 border-blue-500/30",
        textColor: "text-blue-400"
      },
      success: {
        icon: <CheckCircle className="w-8 h-8 text-green-400" />,
        bgColor: "bg-green-500/10 border-green-500/30",
        textColor: "text-green-400"
      },
      failed: {
        icon: <XCircle className="w-8 h-8 text-red-400" />,
        bgColor: "bg-red-500/10 border-red-500/30",
        textColor: "text-red-400"
      }
    };

    const config = statusConfig[paymentStatus];

    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <Card className={`${config.bgColor}`}>
          <CardContent className="p-6 flex items-center gap-4">
            {config.icon}
            <div>
              <p className={`font-semibold ${config.textColor}`}>
                {paymentStatus === "checking" ? "Procesare plată" :
                  paymentStatus === "success" ? "Plată reușită!" : "Plată eșuată"}
              </p>
              <p className={`text-sm ${config.textColor}/70`}>{paymentMessage}</p>
            </div>
            {paymentStatus !== "checking" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPaymentStatus(null)}
                className="ml-auto"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="subscriptions-page">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Înapoi la Dashboard
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <Car className="w-7 h-7 text-white" />
            </div>
            <span className="font-bold text-2xl text-white font-['Archivo']">AUTO CORA</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white font-['Archivo'] mb-2">
            Alege Planul Potrivit
          </h1>
          <p className="text-slate-400 text-lg">
            Plată unică • Fără abonament recurent • 30 de zile acces
          </p>
        </div>

        {/* Payment Status Banner */}
        {renderPaymentStatus()}

        {/* Current Subscription Status */}
        {user?.subscription_status === "active" && !paymentStatus && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="font-semibold text-green-400">Abonament Activ</p>
                  <p className="text-sm text-green-300/70">
                    Plan: {user.subscription_plan?.toUpperCase()}
                    {user.subscription_end && ` • Expiră: ${new Date(user.subscription_end).toLocaleDateString('ro-RO')}`}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Plans Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, index) => {
              const isCurrent = isCurrentPlan(plan.code);
              const colors = planColors[plan.code] || planColors.standard;
              const PlanIcon = planIcons[plan.code] || BookOpen;
              const isPremium = plan.code === "premium";

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={isPremium ? "md:scale-105 z-10" : ""}
                >
                  <Card className={`relative overflow-hidden bg-gradient-to-br ${colors.gradient} ${colors.border} ${isCurrent ? 'ring-2 ring-green-500' : ''} h-full`}>
                    {isPremium && (
                      <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                        RECOMANDAT
                      </div>
                    )}

                    <CardHeader className="pb-4 text-center">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${colors.icon} mx-auto`}>
                        <PlanIcon className="w-8 h-8" />
                      </div>
                      <CardTitle className="text-2xl font-bold text-white font-['Archivo']">
                        {plan.name}
                      </CardTitle>
                      <CardDescription className="text-slate-400">
                        {plan.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-6">
                      <div className="flex items-baseline gap-2 justify-center">
                        {plan.price === 0 ? (
                          <span className="text-4xl font-black text-green-400 font-['Archivo']">GRATUIT</span>
                        ) : (
                          <>
                            <span className="text-5xl font-black text-white font-['Archivo']">{plan.price}</span>
                            <span className="text-slate-400">RON</span>
                          </>
                        )}
                      </div>

                      {plan.price > 0 && (
                        <p className="text-center text-sm text-slate-400">
                          Plată unică • 30 zile acces
                        </p>
                      )}

                      <ul className="space-y-3">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full flex items-center justify-center bg-green-500/20 flex-shrink-0">
                              <Check className="w-3 h-3 text-green-400" />
                            </div>
                            <span className="text-slate-300 text-sm">{feature}</span>
                          </li>
                        ))}

                        {/* Show what's NOT included for Start plan */}
                        {plan.code === "start" && (
                          <>
                            <li className="flex items-center gap-3 opacity-50">
                              <div className="w-5 h-5 rounded-full flex items-center justify-center bg-red-500/20 flex-shrink-0">
                                <X className="w-3 h-3 text-red-400" />
                              </div>
                              <span className="text-slate-400 text-sm line-through">Teorie completă</span>
                            </li>
                            <li className="flex items-center gap-3 opacity-50">
                              <div className="w-5 h-5 rounded-full flex items-center justify-center bg-red-500/20 flex-shrink-0">
                                <X className="w-3 h-3 text-red-400" />
                              </div>
                              <span className="text-slate-400 text-sm line-through">Video explicații</span>
                            </li>
                          </>
                        )}

                        {/* Show what's NOT included for Standard plan */}
                        {plan.code === "standard" && (
                          <li className="flex items-center gap-3 opacity-50">
                            <div className="w-5 h-5 rounded-full flex items-center justify-center bg-red-500/20 flex-shrink-0">
                              <X className="w-3 h-3 text-red-400" />
                            </div>
                            <span className="text-slate-400 text-sm line-through">Video explicații instructor</span>
                          </li>
                        )}
                      </ul>

                      <Button
                        onClick={() => handleActivatePlan(plan.code)}
                        disabled={isCurrent || processingPlan === plan.code}
                        className={`w-full h-12 font-semibold text-white ${isCurrent
                            ? 'bg-green-600 cursor-default'
                            : colors.button
                          }`}
                        data-testid={`subscribe-${plan.code}`}
                      >
                        {processingPlan === plan.code ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : isCurrent ? (
                          <>
                            <Check className="w-5 h-5 mr-2" />
                            Plan Activ
                          </>
                        ) : plan.price === 0 ? (
                          "Activează Gratuit"
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5 mr-2" />
                            Plătește {plan.price} RON
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Info Footer */}
        <div className="mt-12 text-center">
          <Card className="bg-white/5 border-white/10 inline-block">
            <CardContent className="p-4 flex items-center gap-3">
              <Shield className="w-5 h-5 text-blue-400" />
              <span className="text-slate-400 text-sm">
                Plăți securizate prin Stripe • Fără taxare automată • Suport 24/7
              </span>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
